/**
 * Strong-password policy shared by the browser (sign-up / reset-password forms)
 * and the Cloudflare Worker (Better Auth server-side enforcement).
 *
 * Policy (NIST 800-63B aligned): a minimum length plus a check against the
 * HaveIBeenPwned (HIBP) breached-password corpus. No arbitrary character-class
 * composition rules.
 *
 * Uses only Web-standard APIs (`crypto.subtle`, `fetch`, `TextEncoder`) that
 * exist in both the browser and the Workers runtime, so the same module runs on
 * both sides.
 */

export const MIN_PASSWORD_LENGTH = 12;

export const PASSWORD_TOO_SHORT_MESSAGE = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
export const PASSWORD_BREACHED_MESSAGE =
  "This password has appeared in a data breach. Please choose a different one.";

/** Uppercase hex SHA-1 of the input, as required by the HIBP range API. */
async function sha1Hex(password: string): Promise<string> {
  const bytes = new TextEncoder().encode(password);
  const digest = await crypto.subtle.digest("SHA-1", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

/**
 * Check a password against HaveIBeenPwned using k-anonymity: only the first 5
 * chars of its SHA-1 hash ever leave this process. The API returns every
 * breached suffix sharing that prefix; we match the remaining 35 chars locally.
 *
 * Fail-open: on any network/HTTP error we return `false` (treat as not-pwned).
 * A HIBP outage must not block legitimate sign-ups — the length check and Better
 * Auth's own validation still apply.
 */
export async function isPasswordPwned(password: string): Promise<boolean> {
  try {
    const hash = await sha1Hex(password);
    const prefix = hash.slice(0, 5);
    const suffix = hash.slice(5);

    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      // Add-Padding returns extra dummy rows so response size can't reveal how
      // many real matches the prefix has.
      headers: { "Add-Padding": "true" },
    });
    if (!res.ok) {
      console.warn(`HIBP range lookup failed with status ${res.status}`);
      return false;
    }

    const body = await res.text();
    for (const line of body.split("\n")) {
      const [lineSuffix, countStr] = line.trim().split(":");
      if (lineSuffix === suffix && Number(countStr) > 0) {
        return true;
      }
    }
    return false;
  } catch (error) {
    console.warn("HIBP range lookup errored; failing open", error);
    return false;
  }
}

export type PasswordValidationResult = {
  valid: boolean;
  message?: string;
};

/**
 * Full policy check. Length is validated first (cheap, offline) so short
 * passwords never trigger a HIBP request.
 */
export async function validatePassword(
  password: string,
): Promise<PasswordValidationResult> {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return { valid: false, message: PASSWORD_TOO_SHORT_MESSAGE };
  }
  if (await isPasswordPwned(password)) {
    return { valid: false, message: PASSWORD_BREACHED_MESSAGE };
  }
  return { valid: true };
}
